import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Observable, take } from 'rxjs';
import { io, Socket } from 'socket.io-client';
import { ApiService } from './api.service';
import { environment } from 'src/environments/environment';
import { STORAGE, WEBSOCKET } from '../constants/general.constant';
import { cryptoJSData } from '../utils/general.util';
import { Employee } from '../models/employee';
import { isPlatformBrowser } from '@angular/common';

@Injectable({
  providedIn: 'root'
})
export class SocketService {

  private socket!: Socket;

  constructor(private apiServ: ApiService, @Inject(PLATFORM_ID) private platformId: Object,) {
    apiServ.token.subscribe((token) => {
      if(token) {
        apiServ.user$.subscribe((user) => {
          if(user?._id)
            this.initializeSocket(user, token);
        })
      }
    })
  }

  private initializeSocket(user: Employee, token: string) {
    this.socket = io(`${environment.ws.path}`); // Reemplaza con la URL de tu servidor de Socket.IO
    this.socket.on('connect', () => {
      console.log('Connected to server');
      if (isPlatformBrowser(this.platformId)) {
        // Fase 3a.26: el servidor solo une el socket a la sala del empleado identificado por este token
        this.emit('register', { userId: user._id, token });
      }
    })
    
    this.listenAny().subscribe((eventData) => {
      // console.log(`Received event ${eventData.event} with data:`, eventData.data);
      this.setPayloadType(eventData);
    });

    this.listen('disconnect').subscribe((eventData) => {
      console.log('Disconnected from server: ', eventData);
    });

    this.listen('connect_error').subscribe((eventData) => {
      console.error('Error connecting to server: ', eventData);
    });

    this.listen('error').subscribe((eventData) => {
      console.error('Error: ', eventData);
    });

    this.listen('reconnect').subscribe((eventData) => {
      console.log('Reconnected to server: ', eventData);
    });

    this.listen('reconnect_attempt').subscribe((eventData) => {
      console.log('Reconnect attempt: ', eventData);
    });

    this.listen('reconnecting').subscribe((eventData) => {
      console.log('Reconnecting: ', eventData);
    });

    this.listen('reconnect_error').subscribe((eventData) => {
      console.error('Reconnect error: ', eventData);
    });

    this.listen('reconnect_failed').subscribe((eventData) => {
      console.error('Reconnect failed: ', eventData);
    });

    this.listen('ping').subscribe((eventData) => {
      console.log('Ping: ', eventData);
    });

    this.listen('pong').subscribe((eventData) => {
      console.log('Pong: ', eventData);
    });

    this.listen('connect_timeout').subscribe((eventData) => {
      console.error('Connection timeout: ', eventData);
    });

    this.listen('close').subscribe((eventData) => {
      console.error('Connection close: ', eventData);
    });
  }

  // Ejemplo de un método para escuchar eventos
  listen(eventName: string) {
    return new Observable((subscriber) => {
      this.socket.on(eventName, (data) => {
        subscriber.next(data);
      });
    });
  }

  // Método para escuchar cualquier evento
  listenAny(): Observable<{ event: string, data: any }> {
    return new Observable((subscriber) => {
      this.socket.onAny((event, data) => {
        subscriber.next({ event, data });
      });
      // this.socket.prependAnyOutgoing((event, data) => {
      //   console.log('prependAnyOutgoing: ', { event, data });
      // })
    });
  }


  // Ejemplo de un método para emitir eventos
  emit(eventName: string, data: any) {
    this.socket.emit(eventName, data);
  }

  setPayloadType(eventData: any) {
    // console.log('eventData', eventData);
    switch (eventData?.event) {
      case WEBSOCKET.payload_type.employee_update.index: 
        const userAuth = {
          ...eventData?.data
        }
        this.apiServ.setUserData(userAuth); 
        let user = { stsTokenManager: { userAuth: '' } } as any
        user['stsTokenManager']['userAuth'] = userAuth
        window.localStorage.setItem(STORAGE?.user.key, cryptoJSData(true, environment.api.v1.secretKey, JSON.stringify(user)))
        break;

      case WEBSOCKET.payload_type.device_update.index: this.apiServ.setDataSocket(eventData?.data, eventData?.event); break;
      case WEBSOCKET.payload_type.settings.index: this.apiServ.setDataSocket(eventData?.data, eventData?.event); break;
      case WEBSOCKET.payload_type.settings_update.index: this.apiServ.setDataSocket(eventData?.data, eventData?.event); break;
      case WEBSOCKET.payload_type.client_new.index: this.apiServ.setDataSocket(eventData?.data, eventData?.event); break;
      case WEBSOCKET.payload_type.client_update.index: this.apiServ.setDataSocket(eventData?.data, eventData?.event); break;
      case WEBSOCKET.payload_type.tour_update.index: this.apiServ.setDataSocket(eventData?.data, eventData?.event); break;
      case WEBSOCKET.payload_type.tour_create.index: this.apiServ.setDataSocket(eventData?.data, eventData?.event); break;
      case WEBSOCKET.payload_type.travel_update_national.index: this.apiServ.setDataSocket(eventData?.data, eventData?.event); break;
      case WEBSOCKET.payload_type.travel_update_international.index: this.apiServ.setDataSocket(eventData?.data, eventData?.event); break;
      case WEBSOCKET.payload_type.travel_update_customized.index: this.apiServ.setDataSocket(eventData?.data, eventData?.event); break;
      case WEBSOCKET.payload_type.travel_delete.index: this.apiServ.setDataSocket(eventData?.data, eventData?.event); break;
      case WEBSOCKET.payload_type.deposit_new.index: this.apiServ.setDataSocket(eventData?.data, eventData?.event); break;
      case WEBSOCKET.payload_type.deposit_update.index: this.apiServ.setDataSocket(eventData?.data, eventData?.event); break;
      case WEBSOCKET.payload_type.provider_new.index: this.apiServ.setDataSocket(eventData?.data, eventData?.event); break;
      case WEBSOCKET.payload_type.provider_update.index: this.apiServ.setDataSocket(eventData?.data, eventData?.event); break;
      case WEBSOCKET.payload_type.providers.index: this.apiServ.setDataSocket(eventData?.data, eventData?.event); break;
      case WEBSOCKET.payload_type.transport_new.index: this.apiServ.setDataSocket(eventData?.data, eventData?.event); break;
      case WEBSOCKET.payload_type.transport_update.index: this.apiServ.setDataSocket(eventData?.data, eventData?.event); break;
      case WEBSOCKET.payload_type.role_update.index: this.apiServ.setDataSocket(eventData?.data, eventData?.event); break;
      case WEBSOCKET.payload_type.quote_new.index: this.apiServ.setDataSocket(eventData?.data, eventData?.event); break;
      case WEBSOCKET.payload_type.quote_update.index: this.apiServ.setDataSocket(eventData?.data, eventData?.event); break;
    }
  }
  disconnect() {
    this.socket.disconnect();
  }
}
